import React, { useEffect, useState } from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: { flexDirection: "column", padding: 10 },
  section: { margin: 10, padding: 10, flexGrow: 1 },
  heading: { fontSize: 20, marginBottom: 10, textAlign: "center" },
  subHeading: { fontSize: 16, marginBottom: 5, fontWeight: "bold", color: "blue" },
  text: { fontSize: 12, marginBottom: 5 },
  table: { display: "table", width: "auto", borderStyle: "solid", borderWidth: 1, borderRightWidth: 0, borderBottomWidth: 0, marginTop: 10 },
  tableRow: { flexDirection: "row" },
  tableColHeader: { width: "16.66%", borderStyle: "solid", borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, backgroundColor: "#f2f2f2" },
  tableCol: { width: "16.66%", borderStyle: "solid", borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0 },
  tableCellHeader: { margin: 5, fontSize: 12, fontWeight: "bold" },
  tableCell: { margin: 5, fontSize: 10 },
});

const formatDate = (dateString) => {
  if (!dateString) return "Invalid Date";
  const date = new Date(dateString);
  if (isNaN(date)) return "Invalid Date";
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(date);
};

const isCurrentMonth = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
};

const calculateMonthlyAverages = (data) => {
  const sums = {}, counts = {};
  data.forEach((item) => {
    const date = new Date(item.logDate);
    const key = `${date.toLocaleString("default", { month: "short" })} ${date.getFullYear()}`;
    if (!sums[key]) { sums[key] = 0; counts[key] = 0; }
    sums[key] += parseInt(item.score, 10);
    counts[key]++;
  });
  return Object.keys(sums).map((k) => ({ monthYear: k, average: (sums[k] / counts[k]).toFixed(2) }));
};

const answerLabels = { "1": "Itchiness", "2": "Pain", "3": "Redness", "4": "Scaling" };

const MonthlyPdf = ({ logs }) => {
  const [tableData, setTableData] = useState([]);
  const userDetails = logs.userDetails ? logs.userDetails[0] : {};
  const dlqidetails = logs.dlqiScores || [];
  const bsadetails = logs.bsaScores || [];
  const skinDiarydetails = logs.skinDiary || [];
  const skinMooddetails = logs.skinmood || [];
  const flareupdetails = logs.flareup || [];
  const emotionsdetails = logs.emotions || [];
  const waterdetails = (logs.water || []).filter((l) => isCurrentMonth(l.createdTime));
  const activitydetails = (logs.activity || []).filter((l) => isCurrentMonth(l.createdTime));
  const dlqiAvg = calculateMonthlyAverages(dlqidetails);
  const bsaAvg = calculateMonthlyAverages(bsadetails);
  const skinDiaryAvg = calculateMonthlyAverages(skinDiarydetails);

  useEffect(() => {
    const combined = [
      ...flareupdetails.map((i) => ({ ...i, type: "flareup" })),
      ...skinMooddetails.map((i) => ({ ...i, type: "skinmood" })),
      ...emotionsdetails.map((i) => ({ ...i, type: "emotions" })),
      ...waterdetails.map((i) => ({ ...i, type: "water" })),
      ...activitydetails.map((i) => ({ ...i, type: "activity" })),
    ].sort((a, b) => new Date(a.createdTime) - new Date(b.createdTime));

    const grouped = {};
    combined.forEach((log) => {
      const date = formatDate(log.createdTime);
      if (!grouped[date]) grouped[date] = { date, entries: [] };
      grouped[date].entries.push(log);
    });

    Object.keys(grouped).forEach((date) => {
      const waterEntries = grouped[date].entries.filter((e) => e.type === "water");
      if (waterEntries.length > 1) {
        const total = waterEntries.reduce((s, e) => s + parseFloat(e.amount), 0);
        grouped[date].entries = grouped[date].entries.filter((e) => e.type !== "water");
        grouped[date].entries.push({ type: "water", amount: total });
      }
      const actEntries = grouped[date].entries.filter((e) => e.type === "activity");
      if (actEntries.length > 1) {
        const names = actEntries.map((e) => e.name).join(", ");
        grouped[date].entries = grouped[date].entries.filter((e) => e.type !== "activity");
        grouped[date].entries.push({ type: "activity", name: names });
      }
    });

    setTableData(Object.values(grouped));
  }, [logs]);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}><Text style={styles.heading}>Monthly SKIN Report</Text></View>
        <View style={styles.section}>
          <Text style={styles.subHeading}>Personal Details</Text>
          <Text style={styles.text}>Name: {userDetails.fullName}</Text>
          <Text style={styles.text}>Doctor Name: {logs.doctorDetails}</Text>
          <Text style={styles.text}>Age: {userDetails.age}</Text>
          <Text style={styles.text}>Gender: {userDetails.gender}</Text>
          <Text style={styles.text}>Onboarding Date: {formatDate(userDetails.createdTime)}</Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.subHeading}>DLQI SCORE (Out of 30)</Text>
          {dlqidetails.length > 0
            ? dlqidetails.map((item, i) => <Text key={i} style={styles.text}>{formatDate(item.logDate)}: {item.score}</Text>)
            : <Text style={styles.text}>No Data found</Text>}
          {dlqiAvg.map((item, i) => <Text key={i} style={styles.text}>Average of {item.monthYear}: {item.average}</Text>)}
        </View>
        <View style={styles.section}>
          <Text style={styles.subHeading}>BSA% (Out of 100)</Text>
          {bsadetails.length > 0
            ? bsadetails.map((item, i) => <Text key={i} style={styles.text}>{formatDate(item.logDate)}: {item.score}</Text>)
            : <Text style={styles.text}>No Data found</Text>}
          {bsaAvg.map((item, i) => <Text key={i} style={styles.text}>Average of {item.monthYear}: {item.average}</Text>)}
        </View>
        <View style={styles.section}>
          <Text style={styles.subHeading}>DAILY LOGS</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              {["DATE","SKIN MOOD","EMOTION","FLARE-UP","Water (ltr)","Activity"].map((h,i) => (
                <View key={i} style={styles.tableColHeader}><Text style={styles.tableCellHeader}>{h}</Text></View>
              ))}
            </View>
            {tableData.map((group, i) => (
              <View key={i} style={styles.tableRow}>
                <View style={styles.tableCol}><Text style={styles.tableCell}>{group.date}</Text></View>
                <View style={styles.tableCol}>
                  {group.entries.filter((e) => e.type === "skinmood").map((e, idx) => (
                    <Text key={idx} style={styles.tableCell}>{answerLabels[e.questionId]}: {e.answer}</Text>
                  ))}
                </View>
                <View style={styles.tableCol}>
                  {group.entries.filter((e) => e.type === "emotions").map((e, idx) => (
                    <Text key={idx} style={styles.tableCell}>Emotion: {e.baseEmotion}</Text>
                  ))}
                </View>
                <View style={styles.tableCol}>
                  {group.entries.filter((e) => e.type === "flareup").map((e, idx) => (
                    <Text key={idx} style={styles.tableCell}>Flare-up: {e["1"]} Reasons: {Array.isArray(e["2"]) ? e["2"].join(", ") : ""}</Text>
                  ))}
                </View>
                <View style={styles.tableCol}>
                  {group.entries.filter((e) => e.type === "water").map((e, idx) => (
                    <Text key={idx} style={styles.tableCell}>{e.amount}</Text>
                  ))}
                </View>
                <View style={styles.tableCol}>
                  {group.entries.filter((e) => e.type === "activity").map((e, idx) => (
                    <Text key={idx} style={styles.tableCell}>{e.name}</Text>
                  ))}
                </View>
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default MonthlyPdf;
